import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Anchor } from "lucide-react";

const whatWeBelieve = [
  { title: "Fresh comes first", desc: "Seafood is chosen for quality, schnitzels are crumbed fresh daily, and every plate is built to order." },
  { title: "Everyone at the table matters", desc: "From high chairs to whisky glasses, every guest deserves their moment." },
  { title: "Coffee is a craft", desc: "Locally roasted beans, freshly ground, made to order by baristas who care." },
  { title: "Hospitality is generosity", desc: "Fair prices, honest portions and a genuine Frankston welcome." }
];

function PageHeader({ title, subtitle, image }) {
  return (
    <section
      className="relative h-[60vh] min-h-[420px] flex items-center justify-center text-center bg-cover bg-center"
      style={{ backgroundImage: `linear-gradient(rgba(10,36,36,0.7), rgba(10,36,36,0.85)), url(${image})` }}
    >
      <div className="absolute inset-0 grain-overlay" />
      <div className="relative pt-20">
        <span className="divider-ornament text-spice-gold">{subtitle}</span>
        <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mt-5">
          {title}
        </h1>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <PageHeader
        title="Our Story — Bold Flavours, Coastal Soul"
        subtitle="About Sea Spice Kitchen"
        image="/image/siteimages/gallery/2880x2304Selects/SeaSpiceKitchen_Lobster_2880x2304.jpg"
      />

      {/* Story */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <span className="divider-ornament">Welcome</span>
            <h2 className="font-display text-sea-deep text-4xl md:text-5xl font-medium mt-5 mb-6 leading-[1.1]">
              Bold flavours, <em className="italic text-spice-gold font-light">coastal soul</em>
            </h2>
            <p className="text-sea-mid/85 text-base md:text-lg leading-relaxed mb-5">
              Sea Spice Kitchen was born from a simple idea: that the food of the coast deserves a home where everyone feels welcome. Not a formal dining room, not a takeaway counter — something warmer. A neighbourhood kitchen where the seafood is treated with respect, the coffee is taken seriously, and the kids&apos; menu gets as much thought as the wine list.
            </p>
            <p className="text-sea-mid/85 text-base leading-relaxed mb-5">
              Frankston sits where Melbourne meets the Mornington Peninsula, and our menu lives in that same meeting place. We call it coastal fusion: the freshest fish and shellfish cooked with Mediterranean warmth, Australian classics done properly, and a line of heritage curries that carry the spice of home kitchens far from the sea.
            </p>
            <p className="text-sea-mid/85 text-base leading-relaxed">
              It is food without borders, cooked with care.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-spice-gold hidden lg:block" />
            <img
              src="/image/siteimages/gallery/2880x2304Selects/SeaSpiceKitchen_ChefSignatureGarlicCrab_2880x2304.jpg"
              alt="Sea Spice Kitchen signature garlic crab"
              className="relative w-full h-[560px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* What is Coastal Fusion */}
      <section className="py-24 lg:py-32 bg-sand">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="divider-ornament">Our Philosophy</span>
            <h2 className="font-display text-sea-deep text-4xl md:text-5xl lg:text-6xl font-medium mt-5 mb-5 leading-[1.1]">
              What we <em className="italic text-spice-gold font-light">believe</em>
            </h2>
            <p className="text-sea-mid/85 text-base md:text-lg">
              The values behind every coffee, cocktail and plate we serve.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {whatWeBelieve.map((p, i) => (
              <div key={p.title} className="bg-white p-8 border-t-2 border-spice-gold hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="font-display text-sea-deep/30 text-5xl mb-2">0{i + 1}</div>
                <h3 className="font-display text-sea-deep text-xl mb-3">{p.title}</h3>
                <p className="text-sea-mid/75 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cafe, Restaurant, Bar */}
      <section className="py-24 lg:py-32 bg-[#0d2929] text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <span className="divider-ornament text-spice-gold">All Day, Every Day</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium mt-5 mb-6">Cafe. Restaurant. <em className="italic text-spice-gold font-light">Bar.</em></h2>
          <p className="text-white/75 leading-relaxed text-base md:text-lg">
            Through the morning we run as a true local cafe — specialty coffee, sweet and savoury breakfasts, light bites and milkshakes the kids will remember. By lunch and dinner the restaurant takes over, sending out appetisers, seafood mains, pastas, burgers, grills and our signature schnitzel range. And across it all runs our fully licensed bar: signature cocktails, an aperitif and spirits list served in 30ml nips, a whisky collection spanning Scotland to Starward, and wines poured by the glass or bottle — with mocktails and a zero-alcohol chardonnay for the designated drivers.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cream text-center">
        <div className="max-w-3xl mx-auto px-5">
          <Anchor className="mx-auto text-spice-gold mb-5" size={32} />
          <h3 className="font-display text-sea-deep text-3xl md:text-4xl mb-5">
            Visit us
          </h3>
          <p className="text-sea-mid/80 mb-8">You will find us at Shop 1/432 Nepean Hwy, Frankston — an easy stop on the way to the waterfront, with a nautical navy-and-gold room that feels like a holiday from the moment you sit down. Come hungry, bring the family, stay for one more round. We look forward to welcoming you.</p>
          <Link to="/reservations">
            <Button className="bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold rounded-none px-10 h-14">
              Book Your Table
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
